<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter, type RouteLocationMatched } from 'vue-router'
import { ChevronRight } from 'lucide-vue-next'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BreadcrumbItem {
  label: string
  to?: string
}

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

const route = useRoute()
const router = useRouter()

// ---------------------------------------------------------------------------
// Computed
// ---------------------------------------------------------------------------

/**
 * Build breadcrumb trail from route meta.
 *
 * Strategy:
 * 1. If route.meta.breadcrumb exists (array of { label, to? }), use it directly
 * 2. Otherwise, auto-generate from matched routes' pageTitle
 *
 * The first item is always "Home" linking to the dashboard.
 */
const breadcrumbs = computed<BreadcrumbItem[]>(() => {
  const items: BreadcrumbItem[] = []

  // Always start with Home/Dashboard
  items.push({ label: 'Home', to: '/admin/dashboard' })

  // Check for explicit breadcrumb meta
  const metaBreadcrumb = route.meta?.breadcrumb as BreadcrumbItem[] | undefined
  if (metaBreadcrumb && Array.isArray(metaBreadcrumb)) {
    items.push(...metaBreadcrumb)
    return items
  }

  // Auto-generate from matched routes
  const matched = route.matched.filter(
    (r: RouteLocationMatched) => r.meta?.pageTitle && r.path !== '/admin/dashboard',
  )

  for (const match of matched) {
    const label = match.meta.pageTitle as string
    // Last matched route is the current page — no link
    const isLast = match === matched[matched.length - 1]
    items.push({
      label,
      to: isLast ? undefined : match.path,
    })
  }

  return items
})

// ---------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------

function navigate(to: string) {
  router.push(to)
}
</script>

<template>
  <nav aria-label="Breadcrumb" data-testid="admin-breadcrumb">
    <ol class="flex items-center gap-1.5 text-sm">
      <li
        v-for="(crumb, index) in breadcrumbs"
        :key="index"
        class="flex items-center gap-1.5"
      >
        <!-- Separator (not before first item) -->
        <ChevronRight
          v-if="index > 0"
          :size="14"
          class="text-text-muted flex-shrink-0"
          aria-hidden="true"
        />

        <!-- Link or plain text -->
        <button
          v-if="crumb.to && index < breadcrumbs.length - 1"
          class="text-text-secondary hover:text-text-primary transition-colors duration-fast"
          :data-testid="`breadcrumb-link-${index}`"
          @click="navigate(crumb.to)"
        >
          {{ crumb.label }}
        </button>
        <span
          v-else
          class="text-text-primary font-medium"
          :aria-current="index === breadcrumbs.length - 1 ? 'page' : undefined"
          :data-testid="`breadcrumb-current-${index}`"
        >
          {{ crumb.label }}
        </span>
      </li>
    </ol>
  </nav>
</template>
